import { useQueryClient } from '@tanstack/react-query';
import { History, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { type AmbientMetric, ApiError, type Instance, type Job, api, keys, unwrap, useAmbient, useJob, useJobs } from '@/api';
import { formatInterval, timeAgo } from '@/lib/format';

import { Badge, Button, Card, Dialog, Field, Input, Spinner } from './ui';

const DAY = 86_400_000;

/** `<input type="datetime-local">` value in local time. */
function toLocalInput(d: Date): string {
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const n = (v: number) => v.toLocaleString();

function Reasons({ rejected }: { rejected: Record<string, number> }) {
	const entries = Object.entries(rejected);
	if (!entries.length) return <span className="text-fg-muted">none</span>;
	return (
		<span className="flex flex-wrap gap-1">
			{entries.map(([reason, count]) => (
				<Badge key={reason} tone="warn">
					{reason} × {n(count)}
				</Badge>
			))}
		</span>
	);
}

const STATUS_TONE: Record<Job['status'], 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'> = {
	queued: 'neutral',
	running: 'accent',
	done: 'ok',
	failed: 'danger',
	cancelled: 'warn',
	interrupted: 'warn',
};

/** Progress and counts of one backfill job; polls while it runs. */
export function JobProgress({ id }: { id: string }) {
	const job = useJob(id);
	const qc = useQueryClient();
	if (!job.data) return <Spinner />;
	const j = job.data;
	const pct = j.totalTicks ? Math.round((100 * j.doneTicks) / j.totalTicks) : 0;
	const active = j.status === 'queued' || j.status === 'running';
	const cancel = async () => {
		try {
			await unwrap(api.DELETE('/api/v1/jobs/{id}', { params: { path: { id } } }));
			qc.invalidateQueries({ queryKey: keys.job(id) });
		} catch (e) {
			toast.error((e as Error).message);
		}
	};
	return (
		<div className="flex flex-col gap-3 text-sm">
			<div className="flex items-center gap-2">
				<Badge tone={STATUS_TONE[j.status]}>{j.status}</Badge>
				<span className="text-fg-muted">
					{n(j.doneTicks)} / {n(j.totalTicks)} ticks · {j.sensors.length} sensor{j.sensors.length === 1 ? '' : 's'}
				</span>
				{active && (
					<Button size="sm" variant="ghost" className="ml-auto" onClick={cancel}>
						<X className="size-3.5" /> Cancel
					</Button>
				)}
			</div>
			<div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
				<div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
			</div>
			<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
				<dt className="text-fg-muted">Readings sent</dt>
				<dd className="font-mono">
					{n(j.readingsSent)} in {n(j.batchesSent)} batches
				</dd>
				<dt className="text-fg-muted">Accepted</dt>
				<dd className="font-mono">{n(j.accepted)}</dd>
				<dt className="text-fg-muted">Duplicates</dt>
				<dd className="font-mono">{n(j.duplicates)}</dd>
				<dt className="text-fg-muted">Rejected</dt>
				<dd>
					<Reasons rejected={j.rejected} />
				</dd>
			</dl>
			{j.error && <p className="rounded-md bg-danger-soft px-3 py-2 text-xs text-danger">{j.error}</p>}
		</div>
	);
}

/**
 * Backfill one instance (`instance`) or every ambient-bound instance (no `instance`): pick a range
 * and an interval, then follow the job.
 */
export function BackfillDialog({ instance, metricsPerTick, onClose }: { instance?: Instance; metricsPerTick?: number; onClose: () => void }) {
	const now = new Date();
	const [from, setFrom] = useState(toLocalInput(new Date(now.getTime() - 7 * DAY)));
	const [to, setTo] = useState(toLocalInput(now));
	const [intervalS, setIntervalS] = useState('60');
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [jobId, setJobId] = useState<string>();
	const [busy, setBusy] = useState(false);
	const qc = useQueryClient();

	const intervalMs = Math.round(Number(intervalS) * 1000);
	const span = Date.parse(to) - Date.parse(from);
	const ticks = intervalMs > 0 && span > 0 ? Math.floor(span / intervalMs) + 1 : 0;

	const start = async () => {
		setErrors({});
		setBusy(true);
		const body = { from: new Date(from).toISOString(), to: new Date(to).toISOString(), intervalMs };
		try {
			const job = instance
				? await unwrap(api.POST('/api/v1/instances/{id}/backfill', { params: { path: { id: instance.id } }, body }))
				: await unwrap(api.POST('/api/v1/ambient/backfill', { body }));
			setJobId(job.id);
			qc.invalidateQueries({ queryKey: keys.jobs });
		} catch (e) {
			if (e instanceof ApiError) setErrors(e.fieldErrors());
			toast.error((e as Error).message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<Dialog open onClose={onClose} title={instance ? `Backfill ${instance.name}` : 'Backfill all slims-ambient sensors'}>
			{jobId ? (
				<div className="flex flex-col gap-4">
					<JobProgress id={jobId} />
					<div className="flex justify-end">
						<Button onClick={onClose}>Close</Button>
					</div>
				</div>
			) : (
				<form
					className="flex flex-col gap-4"
					onSubmit={(e) => {
						e.preventDefault();
						start();
					}}
				>
					<p className="text-sm text-fg-muted">
						Runs a fresh copy of the generator (same seed, parameters and faults) over the range and posts the readings to slims-ambient. The live sensor is not affected. Running the same range
						again only produces duplicates.
					</p>
					<div className="grid grid-cols-2 gap-3">
						<Field label="From" error={errors['/from']} hint="At most 400 days ago">
							<Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
						</Field>
						<Field label="To" error={errors['/to']}>
							<Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
						</Field>
						<Field label="Interval (s)" error={errors['/intervalMs']} hint={intervalMs > 0 ? `one reading every ${formatInterval(intervalMs)}` : undefined}>
							<Input type="number" min={0.1} step="any" value={intervalS} onChange={(e) => setIntervalS(e.target.value)} />
						</Field>
						<div className="flex flex-col justify-end pb-1 text-sm text-fg-muted">
							{n(ticks)} ticks{instance ? '' : ' per sensor'}
							{metricsPerTick ? ` · ${n(ticks * metricsPerTick)} readings` : ''}
						</div>
					</div>
					{errors['/'] && <p className="text-xs text-danger">{errors['/']}</p>}
					<div className="flex justify-end gap-2">
						<Button onClick={onClose}>Cancel</Button>
						<Button variant="primary" type="submit" disabled={busy || ticks === 0}>
							<History className="size-4" /> Start backfill
						</Button>
					</div>
				</form>
			)}
		</Dialog>
	);
}

/** The ambient bindings of one instance: what was sent, what slims-ambient rejected, backfill. */
export function AmbientSensorCard({ instance, ambientMetrics }: { instance: Instance; ambientMetrics: AmbientMetric[] }) {
	const ambient = useAmbient();
	const [backfill, setBackfill] = useState(false);
	const bindings = instance.bindings.flatMap((b) => (b.config.protocol === 'ambient' && b.enabled ? [b.config] : []));
	if (!bindings.length) return null;
	const sink = ambient.data?.sink;
	const metricsPerTick = bindings.reduce((sum, b) => sum + (b.metrics?.length || ambientMetrics.length), 0);
	return (
		<Card
			title={
				<span className="flex items-center gap-2">
					slims-ambient {ambient.data && (ambient.data.enabled ? <Badge tone="ok">sink on</Badge> : <Badge tone="warn">sink off</Badge>)}
				</span>
			}
			actions={
				<Button size="sm" disabled={!ambient.data?.enabled} title={ambient.data?.enabled ? undefined : 'Set MOCK_AMBIENT_URL on the server'} onClick={() => setBackfill(true)}>
					<History className="size-3.5" /> Backfill…
				</Button>
			}
		>
			{!ambient.data ? (
				<Spinner />
			) : !sink ? (
				<p className="text-sm text-fg-muted">The sink is off. Set MOCK_AMBIENT_URL and MOCK_AMBIENT_TOKEN_FILE on the server to send these readings to slims-ambient.</p>
			) : (
				<div className="flex flex-col gap-4 text-sm">
					{bindings.map((b) => {
						const s = sink.sensors[b.sensorCode];
						return (
							<dl key={b.sensorCode} className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
								<dt className="text-fg-muted">Sensor code</dt>
								<dd className="font-mono">{b.sensorCode}</dd>
								<dt className="text-fg-muted">Readings sent</dt>
								<dd className="font-mono">{n(s?.sent ?? 0)}</dd>
								<dt className="text-fg-muted">Newest reading</dt>
								<dd>{s?.lastReadingAt ? timeAgo(s.lastReadingAt) : '—'}</dd>
								<dt className="text-fg-muted">Rejected</dt>
								<dd>
									<Reasons rejected={s?.rejected ?? {}} />
								</dd>
								{s?.lastRejection && (
									<>
										<dt className="text-fg-muted">Last rejection</dt>
										<dd className="font-mono text-xs">{s.lastRejection}</dd>
									</>
								)}
								{!!s?.rejected.out_of_range && (
									<dd className="col-span-2 mt-1 rounded-md bg-warn/15 px-3 py-2 text-xs">
										slims-ambient accepts{' '}
										{ambientMetrics
											.filter((m) => m.max !== undefined && (!b.metrics?.length || b.metrics.includes(m.metric)))
											.map((m) => `${m.metric} ${n(m.min ?? 0)}–${n(m.max ?? 0)}`)
											.join(', ')}
										. Bring the parameters within these ranges; the Parameters tab offers presets that fit, where the definition has them.
									</dd>
								)}
							</dl>
						);
					})}
					<p className="text-xs text-fg-muted">
						{sink.target} · {n(sink.queued)} queued
						{sink.lastSuccessAt && ` · last delivery ${timeAgo(sink.lastSuccessAt)}`}
					</p>
					{sink.lastError && (
						<p className="rounded-md bg-danger-soft px-3 py-2 text-xs text-danger">
							{sink.lastError}
							{sink.lastErrorAt && ` (${timeAgo(sink.lastErrorAt)})`}
						</p>
					)}
				</div>
			)}
			{backfill && <BackfillDialog instance={instance} metricsPerTick={metricsPerTick} onClose={() => setBackfill(false)} />}
		</Card>
	);
}

/** Settings: the sink's configuration and counters, the fleet backfill and recent jobs. */
export function AmbientSettingsCard() {
	const ambient = useAmbient();
	const jobs = useJobs();
	const [backfill, setBackfill] = useState(false);
	const [open, setOpen] = useState<string>();
	const a = ambient.data;
	const s = a?.sink;
	return (
		<Card
			title="slims-ambient"
			className="lg:col-span-2"
			actions={
				a?.enabled && (
					<Button size="sm" onClick={() => setBackfill(true)}>
						<History className="size-3.5" /> Backfill all…
					</Button>
				)
			}
		>
			{!a ? (
				<Spinner />
			) : !s ? (
				<p className="text-sm text-fg-muted">
					Off. Set MOCK_AMBIENT_URL and MOCK_AMBIENT_TOKEN_FILE (or MOCK_AMBIENT_MODE=mqtt with MOCK_AMBIENT_SITE) to push the readings of sensors with an ambient binding to slims-ambient.
				</p>
			) : (
				<div className="flex flex-col gap-4 text-sm">
					<dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
						<dt className="text-fg-muted">Target</dt>
						<dd className="truncate font-mono text-xs sm:col-span-3" title={s.target}>
							{s.target}
						</dd>
						<dt className="text-fg-muted">Flush</dt>
						<dd className="font-mono">
							{formatInterval(a.flushMs)} · ≤ {n(a.maxBatch)}/batch
						</dd>
						<dt className="text-fg-muted">Queued</dt>
						<dd className="font-mono">{n(s.queued)}</dd>
						<dt className="text-fg-muted">Sent</dt>
						<dd className="font-mono">
							{n(s.readingsSent)} in {n(s.batchesSent)} batches
						</dd>
						<dt className="text-fg-muted">Accepted</dt>
						<dd className="font-mono">{n(s.accepted)}</dd>
						<dt className="text-fg-muted">Duplicates</dt>
						<dd className="font-mono">{n(s.duplicates)}</dd>
						<dt className="text-fg-muted">Retries · failed · dropped</dt>
						<dd className="font-mono">
							{n(s.retries)} · {n(s.failedBatches)} · {n(s.dropped)}
						</dd>
						<dt className="text-fg-muted">Rejected</dt>
						<dd className="sm:col-span-3">
							<Reasons rejected={s.rejected} />
						</dd>
					</dl>
					{s.lastError && (
						<p className="rounded-md bg-danger-soft px-3 py-2 text-xs text-danger">
							{s.lastError}
							{s.lastErrorAt && ` (${timeAgo(s.lastErrorAt)})`}
						</p>
					)}
					{!!jobs.data?.length && (
						<table className="w-full text-xs">
							<thead className="text-left text-fg-muted">
								<tr>
									<th className="py-1 font-medium">Backfill</th>
									<th className="py-1 font-medium">Range</th>
									<th className="py-1 font-medium">Sensors</th>
									<th className="py-1 font-medium">Accepted · dup. · rejected</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border">
								{jobs.data.map((j) => (
									<tr key={j.id} className="cursor-pointer hover:bg-muted" onClick={() => setOpen(j.id)}>
										<td className="py-1.5">
											<Badge tone={STATUS_TONE[j.status]}>{j.status}</Badge> <span className="text-fg-muted">{timeAgo(j.createdAt)}</span>
										</td>
										<td className="py-1.5 font-mono">
											{new Date(j.from).toLocaleDateString()} → {new Date(j.to).toLocaleDateString()}
										</td>
										<td className="py-1.5">{j.sensors.map((x) => x.sensorCode).join(', ')}</td>
										<td className="py-1.5 font-mono">
											{n(j.accepted)} · {n(j.duplicates)} · {n(Object.values(j.rejected).reduce((x, y) => x + y, 0))}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			)}
			{backfill && <BackfillDialog onClose={() => setBackfill(false)} />}
			{open && (
				<Dialog open onClose={() => setOpen(undefined)} title="Backfill job">
					<JobProgress id={open} />
				</Dialog>
			)}
		</Card>
	);
}
